// Contenido del sistema de ayuda contextual (HelpModal).
// Cada clave coincide con un valor de `route.page` (ver src/store/router.js y
// src/components/Menu/Menu.vue) para poder mostrar automáticamente la ayuda
// de la sección donde se encuentra el usuario.
//
// Forma de cada entrada:
// {
//   title:   string            — título mostrado en el modal
//   icon:    string            — clase PrimeIcons (misma que en Menu.vue)
//   group:   string            — categoría usada para agrupar el índice
//   summary: string            — resumen de 1-2 frases (se ve en el índice y como intro)
//   sections: [
//     { heading: string, body?: string[], list?: string[] }
//   ]
// }
export const HELP_CONTENT = {
  salidas: {
    title: "Salidas",
    icon: "pi pi-briefcase",
    group: "Salidas",
    summary: "Organiza las salidas de predicación: fecha, hora, territorios, punto de encuentro y conductor, y consulta el historial de salidas ya realizadas.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "Pulsa 'Agregar Salida' para crear una salida Normal, De Grupo (normal, flexible o continua), eligiendo tipo, día, hora, territorios, punto de encuentro, punto de encuentro en el territorio y conductor.",
        "La tabla muestra todas las salidas creadas, con botones para editar, clonar o eliminar cada una, y puedes alternar entre la vista 'Con fecha' y la de salidas 'Continuas'.",
        "Filtra por rango de fechas, grupo, congregación o enlace para encontrar rápido la salida que buscas, e imprime el listado con el botón de la impresora.",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Agregar Salida' y completa tipo, fecha/hora, territorios y conductor.",
        "Agrega el punto de encuentro (o crea uno nuevo con el botón junto al desplegable) y guarda.",
        "Desde la fila de cada salida puedes editarla, clonarla para repetirla otro día o eliminarla.",
      ] },
      { heading: "Consejos", list: [
        "Usa 'Enlaces' para compartir el listado de salidas con quienes no tienen cuenta en el sistema.",
        "Los horarios más usados aparecen como botones rápidos al elegir la hora.",
      ] },
    ],
  },
  enlacesSalidas: {
    title: "Enlaces de acceso",
    icon: "pi pi-link",
    group: "Salidas",
    summary: "Crea enlaces públicos para compartir tus salidas con quienes no tienen cuenta, y sigue en un mapa en vivo quién está compartiendo su ubicación.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "Cada enlace agrupa un conjunto de salidas y se puede compartir por WhatsApp u otra vía sin necesitar cuenta en el sistema.",
        "Desde cada enlace puedes abrir 'Ver mapa' para el monitor de la salida, 'Ubicaciones' para ver en tiempo real quién está compartiendo su posición, o 'Metadata' para revisar cómo se ve la tarjeta al compartirlo.",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Nuevo enlace', ponle un nombre descriptivo y créalo.",
        "Copia el enlace con el botón de copiar y compártelo con el grupo.",
        "Usa 'Ubicaciones' durante la salida para ver en el mapa a quienes están compartiendo su posición en vivo.",
      ] },
      { heading: "Consejos", list: [
        "El mapa de ubicaciones se actualiza automáticamente, no hace falta recargar.",
        "Puedes eliminar un enlace con el icono de papelera cuando ya no lo necesites.",
      ] },
    ],
  },
  enlacesInformes: {
    title: "Informes de conductores",
    icon: "pi pi-check-square",
    group: "Salidas",
    summary: "Crea un enlace público para que cada conductor informe qué territorios se trabajaron en sus salidas, y sigue qué salidas faltan informar.",
    sections: [
      { heading: "Cómo funciona", body: [
        "El conductor abre el enlace, elige su nombre y ve las salidas que ya pasaron y todavía no informó. En cada una marca, territorio por territorio, si se trabajó o no, y puede agregar una nota.",
        "Los territorios marcados como trabajados quedan registrados automáticamente en el Informe S-13 con la fecha de la salida y el nombre del conductor.",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Nuevo enlace', ponle un nombre y, si quieres, limítalo a algunos conductores.",
        "Elige desde qué fecha se piden informes, para no arrastrar salidas viejas.",
        "Copia el enlace y compártelo con los conductores.",
        "Revisa en 'Estado de informes' qué salidas faltan informar.",
      ] },
      { heading: "Consejos", list: [
        "Si un conductor se equivocó, usa 'Reabrir' en la salida: se borra el informe y vuelve a quedar pendiente.",
        "La página del enlace usa la vista fácil (letra grande y botones amplios).",
      ] },
    ],
  },
  transporteTerritorio: {
    title: "Transporte",
    icon: "pi pi-car",
    group: "Salidas",
    summary: "Dibuja sobre el mapa las líneas de transporte público (colectivo/autobús) que llegan a un territorio, con sus paradas y horarios, y busca cómo llegar desde un punto de encuentro.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "En la pestaña 'Líneas' agregas una línea (número y color), dibujas su recorrido sobre el mapa haciendo clic, y marcas sus paradas.",
        "Puedes definir el sentido de la línea (ida/vuelta) y su horario, deshacer el último punto dibujado o limpiar todo el recorrido.",
        "La pestaña 'Buscar' te ayuda a encontrar cómo ir de un origen a un destino usando las líneas ya cargadas.",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Agregar' línea, indica número y color.",
        "Selecciona la línea y toca el mapa para ir marcando su recorrido; cambia a modo 'Paradas' para marcar dónde se sube o baja la gente.",
        "Pulsa 'Guardar' para conservar los cambios.",
      ] },
      { heading: "Consejos", list: [
        "El botón 'Atrás' te devuelve al editor de la tarjeta del territorio.",
        "Usa el botón de la impresora fuera de esta pantalla si necesitas una copia física del territorio con su transporte.",
      ] },
    ],
  },
  alojamientos: {
    title: "Alojamientos",
    icon: "pi pi-home",
    group: "Salidas",
    summary: "Gestiona los alojamientos disponibles para los grupos invitados durante una salida especial: quién ofrece lugar, cuántas personas y si tiene vehículo.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "La tabla lista cada alojamiento con nombre, WhatsApp del anfitrión, ubicación (con enlace a Google Maps), cantidad de personas que recibe, si tiene vehículo y el grupo de invitados asignado.",
        "Puedes editar o eliminar cada alojamiento desde los iconos de la última columna.",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Nuevo' y completa nombre, WhatsApp, ubicación o coordenadas, cantidad de personas, si dispone de vehículo y el grupo de invitados que se aloja allí.",
        "Usa 'Compartir enlace' para generar un enlace público donde cualquiera puede cargar un alojamiento sin necesitar cuenta.",
      ] },
      { heading: "Consejos", list: [
        "No hace falta anotar a cada persona: basta con un contacto por casa o alojamiento.",
        "Un mismo grupo de invitados puede repartirse en más de un alojamiento.",
      ] },
    ],
  },
  gruposInvitados: {
    title: "Grupos de invitados",
    icon: "pi pi-users",
    group: "Salidas",
    summary: "Administra los grupos de publicadores invitados que vienen de otras congregaciones a una salida especial, y coordínalos por WhatsApp.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "Cada grupo tiene código, cantidad de personas y autos, fechas, zona (congregación), encargado y WhatsApp, y se vincula a los grupos de predicación que verán su información al organizar la salida.",
        "Filtra por zona, texto (código o encargado) o día para encontrar un grupo rápido, y marca si el encargado ya está agregado al grupo de WhatsApp.",
        "Desde cada fila puedes enviar un mensaje de WhatsApp con una plantilla, editar el grupo o eliminarlo.",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Nuevo' para dar de alta un grupo con sus datos, o usa 'Importar CSV' para cargar varios a la vez (descarga primero la 'Plantilla').",
        "Asigna la zona y los grupos de predicación correspondientes para que aparezcan vinculados en las salidas.",
        "Usa el icono de WhatsApp para enviarles el mensaje de coordinación.",
      ] },
      { heading: "Consejos", list: [
        "Un grupo de invitados puede repartirse en más de un alojamiento y aparece en la salida a través de sus grupos de predicación.",
        "Las plantillas de mensaje usadas aquí se configuran en 'Plantillas de mensaje'.",
      ] },
    ],
  },
  listaTerritorio: {
    title: "Territorios",
    icon: "pi pi-sitemap",
    group: "Territorios",
    summary: "El listado principal de todos tus territorios: créalos, edítalos, asígnalos a un publicador y consulta su historial de trabajo y salidas.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "Cada tarjeta muestra el nombre del territorio, su color en el mapa, referencia, notas y si está asignado a alguien (con un enlace para asignarlo si no lo está).",
        "El botón 'Editar' de cada tarjeta despliega opciones adicionales (según el territorio) para abrir el registro de trabajo o ver sus últimas salidas.",
        "Usa el buscador para filtrar por nombre, y 'ImportTerr' al pie de la página para importar territorios desde un archivo.",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Crear' para dar de alta un territorio nuevo, o 'Crear varios' si necesitas cargar muchos a la vez dibujándolos sobre el mapa.",
        "Cambia el color de un territorio directamente desde el selector de color de su tarjeta.",
        "Pulsa 'Asignar' en una tarjeta para anotar a qué publicador se lo entregas.",
      ] },
      { heading: "Consejos", list: [
        "Los territorios de tipo 'Rutas' se distinguen con la etiqueta correspondiente en la tarjeta.",
        "Para ver el detalle completo de asignaciones (historial, devoluciones) usa la sección 'Territorios asignados'.",
      ] },
    ],
  },
  tAsignados: {
    title: "Territorios asignados",
    icon: "pi pi-bookmark",
    group: "Territorios",
    summary: "Gestiona qué publicador tiene cada territorio ahora mismo y consulta el historial completo de asignaciones, tanto por territorio como por persona.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "El formulario superior te permite asignar un territorio libre a un publicador; si ya está asignado, te avisa a quién y no te deja duplicarlo.",
        "La tabla de asignaciones vigentes distingue entre asignaciones 'Privadas' (con botón 'Devolver') y 'Públicas' (con botón para quitar el vínculo).",
        "Más abajo, el historial te permite elegir un territorio para ver quién lo tuvo y cuándo, o elegir una persona para ver qué territorios ha tenido.",
      ] },
      { heading: "Pasos típicos", list: [
        "Elige el territorio y escribe el nombre del publicador, luego pulsa 'Asignar'.",
        "Cuando el publicador termine y devuelva el territorio, pulsa 'Devolver' en su fila para cerrar el período y que quede en el historial.",
      ] },
      { heading: "Consejos", list: [
        "Las asignaciones 'Públicas' no guardan historial al quitarlas; las 'Privadas' sí, para que puedas consultar quién lo trabajó y cuándo.",
      ] },
    ],
  },
  mapaCalor: {
    title: "Mapa de calor",
    icon: "pi pi-th-large",
    group: "Territorios",
    summary: "Visualiza sobre el mapa cuántas veces se ha predicado cada manzana, para detectar de un vistazo qué zonas llevan mucho tiempo sin trabajarse.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "El color de cada manzana va de claro a oscuro según cuántas veces fue predicada; las manzanas nunca trabajadas se marcan con un patrón punteado gris.",
        "Puedes filtrar por congregación (zona), por período (todo el historial, últimos 24/12/6/3 meses) y por horario de la salida (mañana, tarde, noche, sin horario).",
        "Arriba se muestran contadores con el total de manzanas, cuántas nunca se trabajaron y el máximo de veces que se predicó una manzana.",
      ] },
      { heading: "Consejos", list: [
        "Usa el filtro de período corto (por ejemplo, últimos 3 meses) para encontrar rápido zonas descuidadas recientemente.",
        "La leyenda de la esquina explica la escala de colores y el patrón de 'nunca trabajada'.",
      ] },
    ],
  },
  rutas: {
    title: "Rutas",
    icon: "pi pi-directions",
    group: "Territorios",
    summary: "Diseña rutas de predicación (usadas en territorios de tipo Rutas): dibuja el recorrido sobre el mapa, marca puntos y define la logística de vehículos.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "El listado de la izquierda muestra las rutas ya creadas; pulsa una para editarla.",
        "Al crear o editar una ruta, tocas el mapa para ir dibujando su recorrido, buscas direcciones para ubicar puntos, y marcas cada punto (con 'RutasPoints') a lo largo del trazado.",
        "En 'Logística de la Ruta' indicas cuántos vehículos recomendados hacen falta y cuántos publicadores por vehículo.",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Crear', ponle nombre a la ruta y toca el mapa para trazar el recorrido punto por punto.",
        "Ajusta vehículos y publicadores recomendados y pulsa 'Guardar Ruta'.",
        "Usa 'Imprimir' si necesitas una copia física, o 'Eliminar' para borrar la ruta.",
      ] },
      { heading: "Consejos", list: [
        "Puedes volver a dibujar la ruta en cualquier momento con 'Volver a dibujar la ruta' sin perder los puntos ya cargados.",
        "Las rutas creadas aquí luego se seleccionan desde el editor del territorio (sección 'Seleccionar Rutas').",
      ] },
    ],
  },
  mapaTerritorios: {
    title: "Mapa de territorio",
    icon: "pi pi-map",
    group: "Territorios",
    summary: "El mapa general con todos tus territorios superpuestos, para verlos de un vistazo y ajustar cómo se muestran.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "Muestra todos los territorios dibujados sobre el mapa base, centrado según el último territorio visto o el primero de la lista.",
        "Pulsa 'Configurar Mapa' para abrir las opciones de visualización (capas, filtros y controles del mapa).",
      ] },
      { heading: "Consejos", list: [
        "La posición del mapa se guarda automáticamente al moverlo, así vuelves al mismo lugar la próxima vez.",
      ] },
    ],
  },
  estadisticas: {
    title: "Informe S-13",
    icon: "pi pi-chart-line",
    group: "Territorios",
    summary: "Consulta las estadísticas de cobertura de los territorios, en una vista amigable para revisar de un vistazo o en formato imprimible tipo informe S-13.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "Por defecto ves la vista amigable con los datos de cobertura de los territorios.",
        "Pulsa 'Ver formato imprimible S-13' para cambiar a la vista pensada para imprimir con el formato del informe habitual, y 'Volver a vista amigable' para regresar.",
      ] },
    ],
  },
  formTerritorio: {
    title: "Editor de tarjeta de territorio",
    icon: "pi pi-file-edit",
    group: "Territorios",
    summary: "El editor completo de un territorio: nombre, límites y manzanas en el mapa, puntos de encuentro y características (tipo de vehículo, seguridad, productividad, observaciones).",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "En la columna izquierda vas completando cada sección: nombre y detalles, límites del territorio dibujados en el mapa, manzanas (para territorios normales), puntos de encuentro y características generales.",
        "Los botones 'Marcar límites', 'Definir manzanas' y 'Definir puntos de encuentro' activan el modo de dibujo correspondiente sobre el mapa de la derecha.",
        "Desde 'Datos adicionales' saltas directo a 'Estudios iniciados' o 'Transporte' del territorio, y si es de tipo Rutas puedes elegir sus rutas asociadas.",
      ] },
      { heading: "Pasos típicos", list: [
        "Completa nombre, zona y número del territorio.",
        "Pulsa 'Marcar límites' y dibuja el contorno tocando el mapa; luego 'Definir manzanas' para las manzanas internas.",
        "Agrega uno o más puntos de encuentro con su dirección, y completa las características (tipo de vehículo, si es apto con lluvia, seguridad, etc.).",
        "Pulsa 'Guardar' para conservar los cambios, o 'Cancelar' para descartarlos.",
      ] },
      { heading: "Consejos", list: [
        "El icono de papelera junto al mapa borra lo dibujado en el paso activo (límites, manzanas o puntos), no todo el territorio.",
        "Los territorios de tipo Rutas no usan manzanas: en su lugar seleccionas rutas ya creadas en la sección 'Rutas'.",
      ] },
    ],
  },
  tarjetaTerritorio: {
    title: "Tarjeta de territorio",
    icon: "pi pi-id-card",
    group: "Territorios",
    summary: "Registra el trabajo realizado en un territorio: qué manzanas se predicaron, cuándo, con qué conductor y en qué turno, y consulta su historial de informes.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "Muestra el detalle del territorio (referencia, notas, asignación actual) y sus últimas actividades: última vez trabajado, última vez completado y manzanas pendientes.",
        "Al crear un nuevo informe eliges la fecha, marcas las manzanas trabajadas, el conductor y el turno (mañana/tarde/noche); el turno se detecta automáticamente si coincide con una salida programada.",
        "En móvil puedes alternar entre el formulario y el mapa con el botón 'Ver Mapa'.",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa para iniciar un nuevo informe, elige la fecha (el turno se detecta solo si hay una salida asociada) y marca las manzanas trabajadas ese día.",
        "Usa la selección rápida para marcar todas las manzanas, o solo la primera/segunda mitad del territorio.",
        "Guarda el registro; puedes editarlo después desde el historial si necesitas corregir algo.",
      ] },
      { heading: "Consejos", list: [
        "Si el territorio está asignado a alguien, puedes liberarlo desde el enlace 'Liberar' junto a su nombre.",
        "El historial de informes ordenados por fecha te permite ver rápido el avance del territorio.",
      ] },
    ],
  },
  crearTerritorios: {
    title: "Crear territorios masivo",
    icon: "pi pi-clone",
    group: "Territorios",
    summary: "Crea varios territorios a la vez dibujándolos directamente sobre el mapa, en lugar de darlos de alta uno por uno.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "El panel lateral lista cada territorio en edición (borrador) con sus puntos, manzanas y puntos de encuentro; haz clic en uno para seleccionarlo y editarlo.",
        "Puedes mostrar como referencia los territorios y puntos de encuentro que ya existen en la base de datos, para no superponerte con ellos.",
        "Para el territorio activo defines zona, número, referencia, color y el modo de dibujo (límites, manzanas o puntos de encuentro).",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Añadir territorio' para crear un nuevo borrador.",
        "Elige el modo de dibujo y toca el mapa para ir marcando; usa 'Deshacer último punto' si te equivocas.",
        "Completa zona, número, referencia y color, y repite con 'Añadir territorio' para cargar varios seguidos.",
      ] },
      { heading: "Consejos", list: [
        "'Quitar territorio' elimina solo el borrador activo, sin afectar a los demás que estés cargando.",
        "Activa las capas 'Territorios' y 'P. encuentro' para evitar dibujar sobre zonas ya cubiertas.",
      ] },
    ],
  },
  estudiosTerritorio: {
    title: "Estudios iniciados",
    icon: "pi pi-book",
    group: "Estudios",
    summary: "El listado central de estudios bíblicos: registra estudiantes, publicadores, urgencia y ubicación, y consúltalos en tabla o sobre un mapa.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "La tabla muestra territorio, estudiante, publicador, fecha de inicio, grupo, tipo y urgencia de cada estudio; puedes filtrar por territorio, grupo, congregación, urgencia, tipo, si está asignado o si se está atendiendo.",
        "El botón 'Mapa' cambia a una vista con los estudios que tienen ubicación marcada.",
        "'Sin informar' muestra los grupos que todavía no reportaron, y 'Estadísticas' abre un resumen general.",
        "'Enlace por grupo' genera un enlace público para compartir una selección de estudios con un grupo concreto.",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Nuevo estudio' y completa los datos del estudiante, publicador, territorio y demás campos.",
        "Haz clic en una fila de la tabla para ver el detalle completo del estudio.",
        "Usa los filtros superiores para acotar la lista antes de revisar o exportar.",
      ] },
      { heading: "Consejos", list: [
        "La urgencia se muestra como una insignia de color al inicio de cada fila; pasa el cursor para ver el nivel.",
        "Los enlaces por grupo se gestionan en 'Enlaces de estudios' si necesitas revisarlos o eliminarlos después.",
      ] },
    ],
  },
  estudiosDashboard: {
    title: "Enlaces de estudios",
    icon: "pi pi-share-alt",
    group: "Estudios",
    summary: "Crea y administra enlaces públicos para compartir una selección de estudios bíblicos con un grupo, sin que necesiten cuenta en el sistema.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "Cada enlace agrupa un conjunto de estudios; puedes 'Gestionar estudios' para elegir cuáles incluir, filtrando por publicador, estudiante, dirección o congregación.",
        "'Metadata' muestra cómo se ve la tarjeta del enlace al compartirlo (por WhatsApp, Facebook, etc.).",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Nuevo enlace', ponle un nombre y créalo.",
        "Entra a 'Gestionar estudios' y marca con el checkbox cuáles quieres incluir (o usa 'Seleccionar todos los filtrados').",
        "Copia el enlace con el icono de copiar y compártelo.",
      ] },
      { heading: "Consejos", list: [
        "Elimina un enlace con el icono de papelera cuando ya no sea necesario compartirlo.",
      ] },
    ],
  },
  userManagement: {
    title: "Gestión de usuarios",
    icon: "pi pi-users",
    group: "Administración",
    summary: "Administra los usuarios con acceso a tu congregación: quién puede entrar, con qué rol y quién está conectado ahora mismo.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "La tabla lista cada usuario con su rol (miembro o administrador), fecha de alta y si está en línea en este momento.",
        "Arriba se muestran los 'Usuarios activos ahora', con la pantalla en la que se encuentran.",
        "Puedes subir o bajar el rol de administrador de un usuario, o eliminarlo (salvo tu propio usuario).",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Nuevo usuario', completa usuario, contraseña inicial y rol, y créalo.",
        "Usa la flecha hacia arriba/abajo en la fila de un usuario para hacerlo administrador o quitarle ese permiso.",
      ] },
      { heading: "Consejos", list: [
        "No puedes cambiar el rol ni eliminar tu propio usuario desde aquí.",
      ] },
    ],
  },
  adminPanel: {
    title: "Panel de administración",
    icon: "pi pi-shield",
    group: "Administración",
    summary: "Panel exclusivo para superusuarios: administra todas las congregaciones del sistema, sus usuarios y los modelos de permisos que aplican.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "Solo lo ven los usuarios marcados como SUPERUSUARIO; el resto ve un aviso de que la sección no está disponible.",
        "Elige una congregación en el selector para ver sus miembros, fecha de creación y su modelo de permisos, y para agregar o eliminar usuarios de esa congregación.",
        "Las pestañas inferiores listan 'Todas las congregaciones' (con opción de ver detalle o eliminarlas) y 'Modelos de permisos' (para crear nuevos modelos).",
      ] },
      { heading: "Pasos típicos", list: [
        "Selecciona una congregación para ver y gestionar sus usuarios y su modelo de permisos.",
        "Pulsa 'Agregar usuario' para dar de alta a alguien directamente en esa congregación, o cambia el rol o la congregación de un usuario existente desde la tabla.",
        "Pulsa 'Nueva congregación' para crear una desde cero.",
      ] },
      { heading: "Consejos", list: [
        "Cambiar el modelo de permisos de una congregación afecta a todos sus usuarios existentes.",
      ] },
    ],
  },
  plantillasMensaje: {
    title: "Plantillas de mensaje",
    icon: "pi pi-comment",
    group: "Administración",
    summary: "Crea plantillas de texto reutilizables para armar rápido el mensaje de WhatsApp que envías a los grupos invitados.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "Cada plantilla tiene un nombre y un texto con variables que se reemplazan automáticamente por los datos de cada grupo al enviarla.",
        "Puedes editar o eliminar cualquier plantilla desde los iconos de su fila.",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Nueva', ponle un nombre y escribe el mensaje.",
        "Haz clic en una de las variables sugeridas para agregarla al final del mensaje.",
        "Guarda; la plantilla queda disponible al enviar mensajes desde 'Grupos de invitados'.",
      ] },
      { heading: "Consejos", list: [
        "Si todavía no creaste ninguna, el sistema usa una plantilla predeterminada.",
      ] },
    ],
  },
  metadataEnlaces: {
    title: "Metadata de enlaces",
    icon: "pi pi-share-alt",
    group: "Administración",
    summary: "Personaliza cómo se ve la tarjeta de tus enlaces públicos (salidas, estudios, alojamientos) al compartirlos por WhatsApp, Facebook o Telegram: título, descripción e imagen.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "Elige el 'Ámbito' (general o un enlace concreto) y edita título, descripción y nombre del sitio para esa tarjeta; puedes insertar variables que se completan con el dato real de cada enlace.",
        "La vista previa te muestra en tiempo real cómo quedará la tarjeta antes de guardar.",
        "La biblioteca de imágenes te permite subir tus propias imágenes (JPG, PNG, WebP o GIF, hasta 5MB) y elegir cuál usar en cada ámbito.",
      ] },
      { heading: "Pasos típicos", list: [
        "Selecciona el ámbito que quieres personalizar.",
        "Completa título, descripción y, si quieres, sube o elige una imagen.",
        "Pulsa 'Guardar'; si te arrepientes, usa 'Restablecer' para volver a los valores por defecto.",
      ] },
      { heading: "Consejos", list: [
        "El tamaño de imagen recomendado es 1200×630 para que se vea bien en la mayoría de apps.",
        "Si un ámbito no tiene personalización propia, hereda los valores generales (se muestran como placeholder).",
      ] },
    ],
  },
  parametros: {
    title: "Configuración",
    icon: "pi pi-cog",
    group: "Configuración",
    summary: "El centro de configuración de la app: datos de tu congregación, grupos y zonas, conductores, mapa de Google, tu cuenta, seguridad, usuarios y copias de seguridad.",
    sections: [
      { heading: "Qué puedes hacer aquí", body: [
        "Cada tarjeta abre un panel para gestionar un aspecto concreto: Congregación, Grupos de Predicación, Zonas (otras congregaciones), Conductores de salidas, Mapa de Google, Puntos de Encuentro, Mi cuenta, Seguridad (autenticación en dos pasos) y, si tu congregación lo permite, sus Usuarios.",
        "También puedes elegir la posición del menú en escritorio (Lateral o Superior).",
        "Más abajo, en 'Backup', puedes descargar una copia de seguridad de todos tus datos o restaurarlos desde un archivo .territorio.",
      ] },
      { heading: "Pasos típicos", list: [
        "Pulsa 'Gestionar' en la tarjeta correspondiente (por ejemplo, 'Gestionar congregación') para abrir su panel y editar los datos.",
        "Usa 'Descargar datos' periódicamente para tener un respaldo, y 'Restaurar datos' si necesitas recuperar uno.",
      ] },
      { heading: "Consejos", list: [
        "Restaurar un archivo de respaldo reemplaza los datos actuales de territorios, salidas y trabajo: hazlo con cuidado.",
        "Si tienes dudas sobre el sistema, usa el botón 'Escribinos' para contactar por WhatsApp.",
      ] },
    ],
  },
};

// Alias: 'downloadTerritorio' renderiza el mismo componente que 'parametros'.
HELP_CONTENT.downloadTerritorio = HELP_CONTENT.parametros;

// Orden y etiquetas de los grupos en el índice del modal de ayuda.
export const HELP_GROUPS = ["Territorios", "Salidas", "Estudios", "Administración", "Configuración"];
